import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  bulkOperationKey,
  RESOLVED_GRACE_MS,
} from '../store/bulk/operations/slice';
import { BulkOperationItem } from '../store/bulk/operations/types';

/**
 * How long a bulk-actioned row stays hidden while waiting for its
 * `threat_status_update` event. Past this the row comes back, so a dropped or
 * malformed WebSocket event degrades to "stale row" instead of "row nobody can
 * see or action again". WHEEL-359.
 */
export const SUPPRESSION_TTL_MS = 2 * 60 * 1000;

/** How often we re-evaluate the TTL while a batch is in flight. */
const TICK_MS = 15_000;

/**
 * The `bulkOperationKey`s of rows a bulk action has taken out of the queue, and
 * which must therefore not be rendered or selectable.
 *
 * Why this lives in Redux rather than the query cache (WHEEL-359 / SUP-3225):
 * after a bulk action the list is refetched immediately, while the backend has
 * not applied the status change yet. That response still contains the actioned
 * rows and overwrites whatever `removeThreatFromListCache` patched into the
 * cache. Redux survives the refetch, so filtering against it at render time is
 * what actually keeps the rows gone.
 *
 * Two sources, and both are needed:
 *
 * - `pendingOperations` - still waiting on the backend. Bounded by
 *   SUPPRESSION_TTL_MS from `startedAt`, so a dropped WebSocket event degrades
 *   to "stale row" instead of "row nobody can see or action again".
 * - `resolvedOperations` - the backend confirmed it. Still filtered for
 *   RESOLVED_GRACE_MS, because a list request issued before the confirmation
 *   can land after it and put the row back. Dropping suppression the moment the
 *   event arrived was the original SUP-3225 failure, so resolution alone is not
 *   enough.
 *
 * Errored operations are deliberately NOT suppressed - they are pinned to the
 * top of the table in red so the analyst can retry them.
 */
export const useSuppressedThreatKeys = (): Set<string> => {
  const pendingOperations = useSelector(
    (state: any) => state.bulkOperations.pendingOperations,
  ) as Record<string, BulkOperationItem>;
  const resolvedOperations = useSelector(
    (state: any) => state.bulkOperations.resolvedOperations,
  ) as Record<string, string>;

  const [now, setNow] = useState<number>(() => Date.now());

  const pendingWithoutError = useMemo(
    () => Object.values(pendingOperations ?? {}).filter(op => !op?.hasError),
    [pendingOperations],
  );

  const resolvedEntries = useMemo(
    () => Object.entries(resolvedOperations ?? {}),
    [resolvedOperations],
  );

  const suppressed = useMemo(() => {
    const keys = new Set<string>();

    pendingWithoutError.forEach(op => {
      const startedAt = new Date(op.startedAt).getTime();
      if (Number.isNaN(startedAt)) return;
      if (now - startedAt < SUPPRESSION_TTL_MS) {
        keys.add(bulkOperationKey(op.companyId, op.domainId, op.threatId));
      }
    });

    resolvedEntries.forEach(([key, resolvedAt]) => {
      const at = new Date(resolvedAt).getTime();
      if (Number.isNaN(at)) return;
      if (now - at < RESOLVED_GRACE_MS) keys.add(key);
    });

    return keys;
  }, [pendingWithoutError, resolvedEntries, now]);

  // Tick only while something is still being suppressed, so an expiring TTL or
  // grace period actually re-renders. Gating on *live* entries rather than on
  // "the maps are non-empty" matters: resolved entries linger in the store
  // until the next event prunes them, so the latter kept an idle dashboard
  // re-rendering every 15s forever after the first batch. Once the last entry
  // expires this set empties, the effect re-runs, and the interval stops.
  const hasLiveSuppression = suppressed.size > 0;

  useEffect(() => {
    if (!hasLiveSuppression) return;
    const interval = setInterval(() => setNow(Date.now()), TICK_MS);
    return () => clearInterval(interval);
  }, [hasLiveSuppression]);

  // A newly added operation is evaluated against a `now` that can be up to
  // TICK_MS stale. That only ever makes an entry look younger, so it stays
  // suppressed; refresh anyway so expiry timing stays tight.
  useEffect(() => {
    if (pendingWithoutError.length || resolvedEntries.length)
      setNow(Date.now());
  }, [pendingWithoutError, resolvedEntries]);

  return suppressed;
};
